"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.analysisBundleSchema = exports.bundleWorkSchema = exports.bundleStopReasonSchema = exports.BUNDLE_STOP_CAUSES = exports.bundleCheckSchema = exports.bundlesQuerySchema = exports.bundlePartSchema = exports.bundlePartBodySchema = exports.bundlePartParamsSchema = exports.bundleParamsSchema = exports.bundleDiscardSchema = exports.bundleUploadSchema = exports.startBundleUploadBodySchema = exports.BUNDLE_STATUSES = exports.BUNDLE_PART_BYTES = void 0;
// Wire shapes of the Analysis bundle upload and list (CR-004, J-181; CR-004-SPEC.md section 6 "Upload, in
// 8 MiB pieces"), served under `/api/v1/analysis-bundles`. A bundle is sent as 8 MiB parts so no request
// comes near Azure's single-request limits: start (declares the size and sha256), one PUT per part,
// complete (checks the stored parts). No shape here carries a value from the bundle: ids, codes,
// counts, hashes and the file name the uploader gave.
const zod_1 = require("zod");
/** Every part but the last is exactly this long (8 MiB); the server refuses a longer one with 413. */
exports.BUNDLE_PART_BYTES = 8 * 1024 * 1024;
exports.BUNDLE_STATUSES = [
    "UPLOADING",
    "UPLOADED",
    "VALIDATING",
    "VALID",
    "INVALID",
    "LOADED",
    "SUPERSEDED",
];
const SHA256 = /^[0-9a-f]{64}$/;
/** A file name only, never a path (as `uploadLoadQuerySchema`'s). */
const fileNameSchema = zod_1.z
    .string()
    .trim()
    .min(1)
    .max(255)
    .regex(/^[^/\\\p{Cc}]+$/u, "a file name without a path or control characters");
exports.startBundleUploadBodySchema = zod_1.z.object({
    year: zod_1.z.number().int().min(2017).max(2100),
    fileName: fileNameSchema,
    /** The whole bundle's bytes; above `BUNDLE_MAX_BYTES` the start answers 413 BUNDLE_TOO_LARGE. */
    sizeBytes: zod_1.z.number().int().positive(),
    /** Lower-case hex sha256 of the whole bundle; the completion checks the stored parts against it. */
    sha256: zod_1.z.string().regex(SHA256),
});
exports.bundleUploadSchema = zod_1.z
    .object({
    bundleId: zod_1.z.uuid(),
    /** The part length to slice by (8 MiB); parts are numbered from 1. */
    partBytes: zod_1.z.number().int().positive(),
    parts: zod_1.z.number().int().positive(),
    /**
     * True when an unfinished upload of the same sha256 was found and is continued (J-208): `bundleId`
     * is that upload's, not a new one's.
     */
    resumed: zod_1.z.boolean(),
    /** The parts already stored whole, so a resumed upload sends only the others; empty for a new one. */
    storedParts: zod_1.z.array(zod_1.z.number().int().positive()),
})
    .meta({ id: "AcoBundleUpload" });
/** `DELETE /analysis-bundles/:bundleId` (J-208): an unfinished upload, or (J-348) a failed load, discarded with its parts. */
exports.bundleDiscardSchema = zod_1.z
    .object({ bundleId: zod_1.z.uuid(), discarded: zod_1.z.literal(true) })
    .meta({ id: "AcoBundleDiscard" });
exports.bundleParamsSchema = zod_1.z.object({ bundleId: zod_1.z.uuid() });
exports.bundlePartParamsSchema = zod_1.z.object({
    bundleId: zod_1.z.uuid(),
    partNo: zod_1.z.coerce.number().int().min(1),
});
/** The part's bytes, `application/octet-stream`, at most 8 MiB. */
exports.bundlePartBodySchema = zod_1.z
    .custom((value) => Buffer.isBuffer(value) && value.length > 0, "a non-empty part")
    .meta({ type: "string", format: "binary" });
exports.bundlePartSchema = zod_1.z
    .object({
    bundleId: zod_1.z.uuid(),
    partNo: zod_1.z.number().int(),
    sizeBytes: zod_1.z.number().int(),
})
    .meta({ id: "AcoBundlePart" });
exports.bundlesQuerySchema = zod_1.z.object({
    year: zod_1.z.coerce.number().int().min(2000).max(2100).optional(),
});
/**
 * One validation check (CR-004-SPEC.md section 6 Validate, step 4): the reason code it refuses with,
 * PASS or FAIL, its counts, and for `ORG_MISMATCH` the group names and TINs that differ. No value.
 */
exports.bundleCheckSchema = zod_1.z
    .object({
    code: zod_1.z.string(),
    result: zod_1.z.enum(["PASS", "FAIL"]),
    counts: zod_1.z.record(zod_1.z.string(), zod_1.z.number().int()),
    differences: zod_1.z.array(zod_1.z.string()),
})
    .meta({ id: "AcoBundleCheck" });
/** One bundle's metadata, manifest and checks; never its bytes. */
/**
 * J-297 (F-036): why a bundle's check or load stopped before it finished. `cause` is `signal` (the process
 * was told to stop: SIGTERM from Azure scaling or a deploy), `error` (a step threw; `error` is its code,
 * never a message or a value) or `lease` (no heartbeat for `leaseSeconds`: the process ended without
 * notice, out of memory or killed). `step`, `worker` (replica or job execution name), `peakRssMb` and `at`
 * are the last known; `restarted` says the bundle loop started it again (once).
 */
exports.BUNDLE_STOP_CAUSES = ["signal", "error", "lease"];
exports.bundleStopReasonSchema = zod_1.z
    .object({
    work: zod_1.z.enum(["check", "load"]),
    cause: zod_1.z.enum(exports.BUNDLE_STOP_CAUSES),
    signal: zod_1.z.string().nullable(),
    error: zod_1.z.string().nullable(),
    leaseSeconds: zod_1.z.number().int().nullable(),
    step: zod_1.z.string().nullable(),
    worker: zod_1.z.string().nullable(),
    peakRssMb: zod_1.z.number().int().nullable(),
    /** ISO 8601: when the stop was recorded, or the last heartbeat for a lease stop. */
    at: zod_1.z.string(),
    restarted: zod_1.z.boolean(),
})
    .meta({ id: "AcoBundleStopReason" });
/** J-297: where the bundle loop's last check or load of a bundle ran and how much memory it reached. */
exports.bundleWorkSchema = zod_1.z
    .object({
    step: zod_1.z.string().nullable(),
    worker: zod_1.z.string(),
    peakRssMb: zod_1.z.number().int().nullable(),
    attempts: zod_1.z.number().int(),
})
    .meta({ id: "AcoBundleWork" });
exports.analysisBundleSchema = zod_1.z
    .object({
    bundleId: zod_1.z.uuid(),
    year: zod_1.z.number().int(),
    fileName: zod_1.z.string(),
    sizeBytes: zod_1.z.number().int(),
    sha256: zod_1.z.string(),
    isSynthetic: zod_1.z.boolean(),
    status: zod_1.z.enum(exports.BUNDLE_STATUSES),
    /** A reason code (`BUNDLE_CORRUPT`, `ORG_MISMATCH`, …); never a message or a value. */
    errorReason: zod_1.z.string().nullable(),
    /** The manifest line once validated: commit, rule set, gates, counts, hashes; no patient value. */
    manifest: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable(),
    /** The validation's checks in their order, each PASS or FAIL with counts. */
    checks: zod_1.z.array(exports.bundleCheckSchema).nullable(),
    /** The uploading admin's opaque id. */
    uploadedBy: zod_1.z.string().nullable(),
    /** An admin asked to load it and the bundle loop has not yet (J-185 polls while it is true). */
    loadRequested: zod_1.z.boolean(),
    /** ISO 8601. */
    createdAt: zod_1.z.string(),
    uploadedAt: zod_1.z.string().nullable(),
    validatedAt: zod_1.z.string().nullable(),
    loadedAt: zod_1.z.string().nullable(),
    /** J-297: the last stop of its check or load, or null; shown under the status on the Data page. */
    stopReason: exports.bundleStopReasonSchema.nullable(),
    /**
     * J-297: the bundle loop's last work on it (check or load): its step, the replica or job execution,
     * the memory high-water mark in MiB and the starts of the current request; null before any.
     */
    work: exports.bundleWorkSchema.nullable(),
})
    .meta({ id: "AcoAnalysisBundle" });
//# sourceMappingURL=bundles.js.map